import { useContentConfig } from '@ad-lucy/context/adData';
import { StringUtils } from '@ad-lucy/utils';
import { useEffect, useState } from 'react';

import type { Destination } from './types';

const referenceToString = (internalReference: unknown): string | undefined => {
  if (typeof internalReference === 'string') {
    // make sure that internalReference is a valid integer
    const referenceAsInt = StringUtils.toInt(internalReference);
    if (referenceAsInt !== undefined) {
      return referenceAsInt.toString();
    }
  }
  if (typeof internalReference === 'number') {
    return internalReference.toString();
  }

  return undefined;
};

const findDestination = (
  link: unknown,
  internalReference: unknown,
  pageLinkOut: string | undefined | null,
  defaultLinkOut: string | undefined | null,
): Destination => {
  const parsedInternalReference = referenceToString(internalReference);

  if (typeof link === 'string' && link.trim()) {
    return { external: true, target: link };
  }
  if (parsedInternalReference) {
    return { external: false, target: parsedInternalReference };
  }
  if (pageLinkOut?.trim()) {
    return { external: true, target: pageLinkOut };
  }
  if (defaultLinkOut) {
    return { external: true, target: defaultLinkOut };
  }
  return { external: false, target: null };
};

/**
 *
 * @param link Only used if it is a string
 * @param internalReference Only used if it is a number
 * @param pageId The id of the page
 */
export function useDestination(
  link: unknown,
  internalReference: unknown,
  pageId: string | null | undefined,
): Destination {
  const { data: contentConfig } = useContentConfig();
  const defaultLinkOut = contentConfig?.general?.linkOutDefault;

  const [pageLinkOut, setPageLinkOut] = useState<string>();
  const [destination, setDestination] = useState(
    findDestination(link, internalReference, pageLinkOut, defaultLinkOut),
  );

  useEffect(() => {
    if (!pageId) {
      setPageLinkOut(undefined);
      return;
    }

    const page = contentConfig?.pages.find((p) => p.id === pageId);
    if (page) {
      setPageLinkOut(page.meta.stageLinkOutDefault);
    }
  }, [contentConfig, pageId]);

  useEffect(() => {
    const locatedDestination = findDestination(
      link,
      internalReference,
      pageLinkOut,
      defaultLinkOut,
    );
    if (
      locatedDestination.external !== destination.external ||
      locatedDestination.target !== destination.target
    ) {
      setDestination(locatedDestination);
    }
  }, [destination, link, internalReference, defaultLinkOut, pageLinkOut]);

  return destination;
}
